// English master dictionary. Every user-visible string lives here.
// Structure mirrors page/component boundaries so translators can scan
// one section at a time. Keep the keys in the same order in tr.ts.

const en = {
  // ─── Nav + footer ────────────────────────────────────────────
  nav: {
    home: "Home",
    how_it_works: "How it works",
    strategy_lab: "Strategy Lab",
    tokens: "Signal history",
    open_telegram: "Open on Telegram →",
    follow_x: "Follow on X",
    toggle_menu: "Toggle menu",
  },
  footer: {
    tagline: "Built for Base, Ethereum, Solana, Robinhood, BNB Chain & Arc.",
  },

  // ─── Landing ─────────────────────────────────────────────────
  landing: {
    live_badge: "Live on Base, Ethereum, Solana, Robinhood, BNB & Arc",
    hero_title_lead: "Copy-trade",
    hero_title_accent: "smart wallets",
    hero_title_tail: "automatically",
    hero_sub:
      "Real-time Telegram alerts the moment proven traders buy a token. Optional auto-execution with the size you choose. Backtested daily.",
    cta_start: "Start on Telegram →",
    cta_open_lab: "Open Strategy Lab",

    stat_signals: "Signals (30d)",
    stat_strategies: "Strategies tested",
    stat_chains: "Chains",
    stat_monitoring: "Monitoring",
    stat_monitoring_value: "24/7",

    features_title: "Built for traders who don't want to babysit charts",
    features_sub:
      "AlphaHedge watches wallets that have proven they pick winners. You get the signal before the price moves.",
    feature_smart_title: "Smart wallet detection",
    feature_smart_body:
      "100+ curated wallets, monitored in real-time. When ≥3 of them buy the same token, you get an instant alert.",
    feature_auto_title: "Optional auto-execute",
    feature_auto_body:
      "Flip a switch and the bot trades for you on every confirmed signal: your size, your slippage, your stop.",
    feature_filters_title: "Filters that protect you",
    feature_filters_body:
      "Market cap floors, liquidity minimums, token age caps: every signal is screened against your risk profile.",
    feature_discover_title: "Discover what works",
    feature_discover_body:
      "Daily auto-discovery analyzes 30 days of signals to surface the most profitable filter combinations.",
    feature_multichain_title: "Multi-chain",
    feature_multichain_body:
      "Base, Ethereum, Solana, Robinhood, BNB Chain and Arc supported out of the box. One bot, every ecosystem, one dashboard.",
    feature_telegram_title: "Telegram-native",
    feature_telegram_body:
      "No app to install, no extension to trust. Use the chat you already have open all day.",

    how_title: "How it works",
    how_sub: "Three steps, fully automated.",
    how_step1_title: "We watch the wallets",
    how_step1_body:
      "Our system monitors on-chain swaps from 100+ proven smart-money wallets across Base, Ethereum, Solana, Robinhood, BNB Chain and Arc 24/7.",
    how_step2_title: "We confirm a signal",
    how_step2_body:
      "When N wallets independently buy the same token within your time window, the bot fires a structured alert.",
    how_step3_title: "You trade or it does",
    how_step3_body:
      "Click to copy, or enable auto-mode and the bot opens the position for you using your chosen ETH size.",
    how_full_link: "Read the full breakdown →",

    final_cta_title: "Ready to stop missing the move?",
    final_cta_sub:
      "Open the bot, set your filters, and let the smart money tell you when to act.",
    final_cta_button: "Open AlphaHedge on Telegram →",
  },

  // ─── How it works page (visual, screenshot-based) ────────────
  how: {
    // Hero
    live_badge: "Live on Base, Ethereum, Solana, Robinhood, BNB & Arc",
    hero_title: "How to use AlphaHedge",
    hero_sub: "Get started with signals, then explore optional automated trading. Follow the product screens below at your own pace.",

    // Progress bar
    prog_1: "Open Bot",
    prog_2: "Signals",
    prog_3: "Copy Trade",
    prog_4: "Fine-Tune",
    prog_5: "Quick Buy",
    prog_6: "Strategy Lab",
    prog_7: "Trade!",

    // Step 1 - Open Bot
    step1_tag: "Getting Started",
    step1_title: "Open the bot on Telegram",
    step1_p1:
      "Tap /start and you'll see the main dashboard (your command center). Signals, wallets, positions, copy trade: everything is one tap away.",
    step1_p2:
      "The bot shows your subscription status and gives quick access to every feature.",
    pill_signals: "My Signals",
    pill_copytrade: "Copy Trade",
    pill_wallets: "Trading Wallets",
    pill_positions: "Open Positions",

    // Step 2 - Signal Filters
    step2_tag: "Configure Signals",
    step2_title: "Set your signal filters",
    step2_p1:
      "Tap \"My Signals\" to control which alerts you receive. Pick active chains, set the minimum smart-wallet consensus, toggle buy/sell signals on or off.",
    step2_p2:
      "Dial in MCAP range, token age limits, enable High Activity whale alerts. Every signal is screened against your profile before it reaches you.",
    pill_chain: "Chain Selection",
    pill_min_wallets: "Min Wallets: 3+",
    pill_mcap: "MCAP Range",
    pill_age: "Age Limits",

    // Copy Trade divider
    copytrade_divider: "Create a Copy Trade Strategy",

    // Wizard section
    wizard_tag: "5 Quick Steps",
    wizard_title: "Build your first trading strategy",
    wizard_sub: "Choose a wallet, set your filters and define a position size. Review your settings before funding and enabling automated trading.",
    wiz_solana_note:
      "Prefer Solana? Same idea, its own dedicated flow: pick a Solana wallet, set your filters, and the bot trades for you via Jupiter. No chain picker needed — it's Solana-only by design.",

    // Wizard cards
    wiz1_tag: "Select",
    wiz1_title: "Pick your wallet",
    wiz1_desc: "Choose which trading wallet the strategy should execute from.",
    wiz2_tag: "Step 1/4",
    wiz2_title: "Min Smart Wallets",
    wiz2_desc: "Set how many wallets must buy before the strategy fires (3–20).",
    wiz3_tag: "Step 2/4",
    wiz3_title: "Choose chain",
    wiz3_desc: "Choose from Base, Ethereum, Robinhood and BNB Chain. Available settings follow the selected chain.",
    wiz4_tag: "Step 3/4",
    wiz4_title: "Amount per trade",
    wiz4_desc: "Set the native-token amount: ETH on Base, Ethereum and Robinhood, or BNB on BNB Chain.",
    wiz5_tag: "Step 4/4",
    wiz5_title: "Name it",
    wiz5_desc: "Pick a name to identify this strategy (up to 64 characters).",

    // Result
    result_tag: "Done!",
    result_title: "Your strategy is live",
    result_sub:
      "Once created, the strategy starts copying signals immediately. Fine-tune everything from the settings panel.",

    // Duo showcase
    step6_tag: "Step 6",
    step6_title: "Strategy created & active",
    step6_desc:
      "Your strategy goes live instantly. The bot starts copying matching signals to your wallet (pause or resume anytime).",
    step7_tag: "Step 7",
    step7_title: "Full strategy settings",
    step7_desc:
      "Trigger filters, buy settings, sell TP/SL, signal sources, gas & slippage: granular control over every parameter.",

    // Bonus divider
    bonus_divider: "Bonus Feature",

    // Quick Buy
    qb_tag: "Quick Buy",
    qb_title: "Place trades from a signal",
    qb_p1: "Enable Quick Buy and configure up to 4 preset amounts. One tap submits a swap without a separate confirmation screen. Review the wallet, chain and amount first.",
    qb_p2: "Set slippage and gas limits, then choose your default wallet. Private transaction submission is optional on supported, configured chains; it cannot guarantee protection from MEV.",
    qb_pill1: "1-Tap Buy",
    qb_pill2: "4 Presets",
    qb_pill3: "MEV Protect",
    qb_pill4: "Custom Gas",

    // Strategy Lab
    lab_divider: "Strategy Lab",
    lab_tag: "Strategy Lab",
    lab_title: "Backtest before you deploy",
    lab_p1:
      "Run what-if simulations against the last 90 days of real signals. Tweak filters (min smart wallets, MCAP range, token age, chain) and instantly see PnL, win rate, median multiplier and hit distribution.",
    lab_p2: "Compare a hypothetical peak-based exit with a daily HODL price snapshot. These models describe historical signals, not executed trades or future returns.",
    lab_pill1: "90-Day Backtest",
    lab_pill2: "What-If PnL",
    lab_pill3: "Hit Distribution",
    lab_pill4: "HODL Reality Check",
    lab_cta: "Open Strategy Lab",

    // Token Protection
    shield_divider: "Token Protection",
    shield_tag: "Multi-Layer Security",
    shield_title: "Risk checks with clear limits",
    shield_sub: "Available checks flag contract and trading risks. Coverage varies by chain and provider, and a successful scan cannot guarantee a safe or profitable trade.",

    shield_how_title: "How it works",
    shield_how_p1: "Base, Ethereum and BNB Chain use GoPlus Security and honeypot.is checks. Solana uses RugCheck. These providers do not currently provide the same coverage for Robinhood or Arc.",
    shield_how_p2: "Results are cached for 5 minutes. If a check is unavailable or fails, some trading paths can continue without a completed assessment. An unavailable result is not evidence that a token is safe.",

    shield_levels_title: "Risk levels",
    shield_critical_title: "Critical: copy-trade blocked",
    shield_critical_desc: "A critical assessment blocks the copy-trade and triggers a Telegram notification. These flags indicate severe risk; they are not a complete security audit.",
    shield_critical_1: "Honeypot detected (buy→sell simulation fails)",
    shield_critical_2: "Sell tax exceeds 30%",
    shield_critical_3: "Token flagged as honeypot by static analysis",
    shield_critical_4: "Token cannot be fully sold",

    shield_high_title: "High: skipped by default",
    shield_high_desc: "The signal is delivered with a warning banner. Copy-trade is skipped unless you explicitly enable 'Allow Risky Tokens' in your strategy settings.",
    shield_high_1: "Owner can change tax rates at any time",
    shield_high_2: "Per-wallet custom tax can be applied",
    shield_high_3: "Hidden contract ownership",
    shield_high_4: "1 or fewer token holders",
    shield_high_5: "Creator holds more than 90% of supply",

    shield_clean_title: "No flagged issues",
    shield_clean_desc: "Available checks found no blocking issues. Other trade rules still apply. This does not guarantee safety, liquidity or a successful exit.",

    shield_extra_title: "Additional safeguards",
    shield_extra_1_title: "Circuit Breaker",
    shield_extra_1_desc: "If a token causes repeated transaction failures, it's automatically blocked for 1 hour across all users.",
    shield_extra_2_title: "Price Sanity Check",
    shield_extra_2_desc: "Before every buy, the bot compares the DEX quote to market price. Extreme deviations (illiquid pools, stale quotes) are rejected.",
    shield_extra_3_title: "Liquidity Floor",
    shield_extra_3_desc: "Configured liquidity thresholds can exclude shallow pools. They do not guarantee an executable exit or prevent all slippage.",
    shield_extra_4_title: "MEV Protection",
    shield_extra_4_desc: "Where supported and configured, private transaction submission can reduce public mempool exposure. Relay failures may fall back to public submission.",

    shield_info_title: "Informational flags",
    shield_info_desc: "These appear in signal messages for transparency but don't block trades. They're common in legitimate tokens too.",
    shield_info_1: "Transfer can be paused (OpenZeppelin standard)",
    shield_info_2: "Blacklist function exists (used by USDC/USDT)",
    shield_info_3: "Token is mintable",
    shield_info_4: "Trading cooldown active",

    // FAQ (kept from original)
    faq_title: "FAQ",
    faq_sub: "The questions everyone asks before connecting a wallet.",
    faq_q1: "Do I have to give the bot my private keys?",
    faq_a1: "No wallet or private key is needed for alerts. For automated trading, the bot stores the trading wallet’s private key encrypted and decrypts it to sign transactions. Use a separate wallet funded only with the amount you intend to trade.",
    faq_q2: "Which chains are supported?",
    faq_a2:
      "Base, Ethereum, Solana, Robinhood, BNB Chain and Arc. All are watched simultaneously and signals are tagged by chain. Automated copy-trading runs on Base, Ethereum, Robinhood, BNB Chain & Solana. Arc is signals-only for now.",
    faq_q3: "What does 'smart wallet' mean here?",
    faq_a3:
      "A wallet that has demonstrably picked winners. We score wallets on realized PnL across confirmed exits, not unrealized paper gains. Wallets that fall below threshold are rotated out.",
    faq_q4: "Can I see historical performance before signing up?",
    faq_a4_lead: "Yes, visit the ",
    faq_a4_link: "Strategies",
    faq_a4_tail:
      " page. It shows the top filter combinations from the last 30 days of real signals, refreshed daily.",
    faq_q5: "What does the backtest cap mean?",
    faq_a5: "The model caps each hypothetical exit multiplier at 10× to limit outlier influence. This is not a simulation of available liquidity, slippage or execution. Historical peak values and model PnL are not realized trading returns.",

    bottom_cta_title: "See it for yourself",
    bottom_cta_sub:
      "Open the bot on Telegram; first signals are free to receive.",
    bottom_cta_button: "Open in Telegram",
  },

  // ─── Strategy Lab page ───────────────────────────────────────
  lab: {
    page_label: "Historical simulation",
    page_title: "Strategy Lab",
    page_sub: "Explore up to 90 days of historical signals. Compare a daily price snapshot with hypothetical peak-based exits. Results are simulations, not executed trades. Tracked wallet identities stay private.",

    error_rate_limit:
      "Rate limit reached. Please wait a moment before adjusting filters again.",
    error_backtest_failed: "Backtest failed: {msg}",

    whatif_header: "Modeled exit results",
    running: "running…",

    top_title: "Top {n} historical strategies",
    top_sub: "The {n} filter combinations ranked by modeled PnL over {days} days. Refreshed daily, independently of your filters. Rankings use hindsight and do not predict future returns.",
    top_last_updated: "Last updated:",
    top_snapshot_generating: "Snapshot is generating. Refresh in a moment.",
    top_load_error: "Couldn't load: {msg}",
    top_loading: "Loading strategies…",
    top_empty: "Snapshot is empty. Check back after the next daily refresh.",

    col_filter: "Filter",
    col_signals: "Signals",
    col_median: "Median ×",
    col_win_rate: "Win rate",
    col_2x5x: "2× / 5×",
    col_pnl: "Model PnL ($)",

    cta_title: "Want to actually trade these signals?",
    cta_sub:
      "Open the bot on Telegram, apply your filter, and start receiving real-time alerts.",
    cta_button: "Open in Telegram",
  },

  // ─── Filter Panel ────────────────────────────────────────────
  filter: {
    header: "Filters",
    reset: "Reset",
    lookback_days: "Lookback (days)",
    min_smart_wallets: "Min smart wallets",
    min_entry_mc: "Min entry MC (USD)",
    max_entry_mc: "Max entry MC (USD)",
    max_peak_mc: "Max peak MC (USD) (outlier cap)",
    min_liquidity: "Min liquidity (USD)",
    max_liquidity: "Max liquidity (USD)",
    max_token_age: "Max token age at signal",
    chains_label: "Chains:",
    exit_model: "Hypothetical exit model",
    exit_at_peak: "Exit at % of peak",
    per_trade_size: "Per-trade size ($)",
    suffix_min: "min",
    quality_label: "Signal quality:",
    quality_neutral: "Neutral",
    quality_hint:
      "The ★ rating every signal carries in the bot, set by token age and liquidity at signal time.",
  },

  // ─── Results Card ────────────────────────────────────────────
  results: {
    model_note: "Hypothetical exits at a chosen share of the recorded peak. Exit multiplier cap: {cap}. Modeled exit fee: {fee}%. Gas, slippage, liquidity and execution delays are not modeled.",
    no_cap: "No cap",
    empty_state: "Adjust filters to run a backtest.",
    signals: "Signals",
    win_loss: "{w} win / {l} loss",
    win_rate: "Win rate",
    win_rate_hint: "2×: {p2}% / 5×: {p5}% / 10×: {p10}%",
    median_multiplier: "Median multiplier",
    median_hint: "avg {avg}× (outlier-skewed)",
    exit_label: "Exit @ {pct}% peak",
    exit_hint: "invested ${inv} · ROI {sign}{roi}%",
    pnl_chart_title: "Cumulative model PnL",
    no_signals: "no qualifying signals",
    hit_chart_title: "Peak multiplier distribution",
    hit_chart_disclaimer_line1: "Observed historical peaks.",
    hit_chart_disclaimer_line2: "Not realized trading returns.",
  },

  // ─── HODL Panel ──────────────────────────────────────────────
  hodl: {
    title: "HODL snapshot",
    sub_lead: "A historical model: buy each qualifying signal and ",
    sub_strong: "hold until the snapshot",
    sub_tail: ". Prices are captured from DexScreener during the daily refresh. A fall below entry is shown as less than 1×. This is not a live portfolio or an executed return.",
    updated: "Updated:",
    slider_label: "Min smart wallets",
    days_label: "Window",
    quality_label: "Quality",
    loading_inline: "loading…",
    snapshot_generating: "HODL snapshot is generating. Refresh in a moment.",
    load_error: "Couldn't load: {msg}",
    loading: "Loading HODL snapshot…",
    empty_at_threshold:
      "No tokens matched {n}+ smart wallets and the selected quality in the last {days} days. Try other filters.",
    stat_tokens_with_price: "Tokens (with price)",
    stat_tokens_with_price_hint_missing: "{n} unavailable prices excluded",
    stat_tokens_with_price_hint_all: "prices available for all tokens",
    stat_win_rate: "Win rate (>1×)",
    stat_win_rate_hint: "{w} up / {l} down",
    stat_median: "Median multiplier",
    stat_median_hint: "snapshot MC ÷ entry MC",
    stat_pnl: "Model PnL",
    stat_pnl_hint: "invested ${inv}",
    description: "${size} per signal. Tokens with unavailable prices are excluded from PnL, win rate and distribution, not counted as zero. Gas, slippage and execution limits are not modeled.",
    distribution_title: "HODL multiplier distribution",
  },

  // ─── Tokens page ─────────────────────────────────────────────
  tokens: {
    page_label: "Signal history",
    page_title: "Selected runners",
    page_sub: "Historical runner highlights with a 24-hour delay. Only signals with a recorded peak ratio of at least 1.5× qualify. These merged figures are not a complete performance record or realizable trading returns.",
    chain_label: "Chain",
    chain_all: "All",
    sort_label: "Sort",
    sort_top: "Highest peak ratio",
    sort_recent: "Most recent",
    window_label: "Window",
    window_all: "All",
    wallets_label: "Min wallets",
    loading_inline: "loading…",
    error_rate_limit: "Rate limit reached. Please wait a moment.",
    error_generic: "Couldn't load: {msg}",
    loading: "Loading tokens…",
    empty_title: "No selected runners found",
    empty_sub: "Try a longer window, lower the minimum wallet count or select all chains.",
    col_token: "Token",
    col_chain: "Chain",
    col_mc_signal: "Min signal MC",
    col_peak_mc: "Max peak MC",
    col_peak_mult: "Peak ratio",
    col_age: "Record age",
    age_today: "today",
    age_one_day: "1d ago",
    age_n_days: "{n}d ago",
    footnote: "{count} selected runners shown · {days}-day window · 24h delay",
    footnote_all: "{count} selected runners shown · available history · 24h delay",
    pagination_prev: "‹ Prev",
    pagination_next: "Next ›",
    pagination_page: "page {n}",
    cta_title: "Follow new signals in Telegram",
    cta_sub: "Get token details, choose your filters and explore optional automated trading on supported chains.",
    cta_button: "Open in Telegram",
    methodology_note: "Peak ratio = highest recorded peak market cap ÷ lowest qualifying signal market cap for the token in the selected records. The two values may come from different signals; their order does not establish a buy-then-sell return. Record limits and filters for liquidity or severe price declines can omit tokens from this selection.",
  },

  // ─── Misc ────────────────────────────────────────────────────
  misc: {
    just_now: "just now",
    min_ago: "{n} min ago",
    hr_ago: "{n} hr ago",
    days_ago: "{n} days ago",
    unknown: "unknown",
    retry: "Try again",
  },
};

export default en;
